import React, {FC, useEffect, useState} from "react";
import axios from "axios";
import dxHtmlEditor from "devextreme/ui/html_editor";
import HtmlEditor, { Toolbar, MediaResizing, Item } from "devextreme-react/html-editor";
import {useTypedSelector} from "../reducers";
import { LoadPanel } from "devextreme-react/load-panel";
import { useNavigate } from "react-router-dom";
import { handleError } from "../error/ErrorHandler";

type Props = {
    menuId: number;
    ctgId: number;
    brdId?: number;
}

type BoardPostContentType = {
    brdContent: string;
    brdId: number;
    brdSeq: number;
    brdTitle: string;
    brdWriter: string;
    createdDtm: string;
    ctgId: number;
    hideYn: string;
    updatedDtm: string;
    usrNm: string;
}

const BoardEditContent: FC<Props> = ({menuId, ctgId, brdId}) => {

    const [ brdTitle, setBrdTitle ] = useState('');
    const [ brdContent, setBrdContent ] = useState('');
    const [ loading, setLoading ] = useState(false);
    const { isLogin, loginUser, status, isAdmin } = useTypedSelector(state => state.auth);
    const navigate = useNavigate();

    const sizeValues = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];
    const fontValues = ['Arial', 'Courier New', 'Georgia', 'Impact', 'Lucida Console', 'Tahoma', 'Times New Roman', 'Verdana'];
    const headerValues = [false, 1, 2, 3, 4, 5];

    let data = {
        brdTitle: brdTitle,
        brdContent: brdContent,
        ctgId: ctgId
    }

    const handleChange = (e: any) => {
        if (e.target.value) data.brdTitle = e.target.value;
    }

    const getBoardyCategory = async() => {
        if(!ctgId) return;
        try {
            const res = await axios.get(`/api/board/ctg/one/${ctgId}`);
            const category = res.data;
            if(!isLogin || (!category.usrWrtYn && !isAdmin)) {
                return goHome();
            }
        } catch(err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const goHome = () => {
        alert("권한이 없습니다.");
        return navigate("/");
    }

    const onValueChanged = (e: any) => {
        if (e.value) data.brdContent = e.value;
    }

    const goToList = () => {
        navigate(`/board/${menuId}`);
    }

    const loadContent = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/board/post/one/${brdId}`);
            if (res.data) {
                if(!isAdmin && res.data.brdWriter !== loginUser?.usrId) {
                    return goHome();
                }
                setBrdTitle(res.data.brdTitle);
                setBrdContent(res.data.brdContent);

                const element = document.getElementById('htmlEditor');
                if (element) dxHtmlEditor.getInstance(element).option('value', brdContent);
            }
        } catch(err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    }

    const saveContent = async () => {
        setLoading(true);
        let result = window.confirm("작성한 내용을 저장하시겠습니까?");
        if(result){
            try {
                await axios.post(`/api/board/post`, data)
                goToList();
            } catch(err) {
                handleError(err);
            } finally {
                setLoading(false);
            }
        } else setLoading(false);
    }

    const updateContent = async () => {
        setLoading(true);
        let result = window.confirm("변경한 내용을 저장하시겠습니까?");
        if(result){
            try {
                await axios.put(`/api/board/post/${brdId}`, data);
                goToList();
            } catch(err) {
                handleError(err);
            } finally {
                setLoading(false);
            }
        } else setLoading(false);

    }

    useEffect(() => {
        if(status === 'idle') return;
        getBoardyCategory();
        if (brdId) {
            loadContent();
        } else {
            setLoading(false);
        }
    }, [menuId, ctgId, brdId, brdTitle, brdContent, status]);

    return (
        <div className={'board-edit-wrapper'}>
            { loading && 
                <LoadPanel visible={true} />
            }
            { !loading && 
                <div className={'board-edit'}>
                    <div className={'board-edit-title-wrapper'}>
                        <label>
                            <span className={'board-edit-title-label'}>글제목</span>
                            <input className={'board-edit-title-input'} type={'text'} onChange={handleChange} defaultValue={brdTitle} />
                        </label>
                    </div>
                    <div className={'board-edit-html-wrapper'}>
                        <HtmlEditor id='htmlEditor'
                                    height={800}
                                    onValueChanged={onValueChanged}
                                    valueType={'html'}
                                    defaultValue={data.brdContent}>
                            <MediaResizing enabled={true} />
                            <Toolbar multiline={true}>
                                <Item name="undo" />
                                <Item name="redo" />
                                <Item name="separator" />
                                <Item
                                    name="size"
                                    acceptedValues={sizeValues}
                                />
                                <Item
                                    name="font"
                                    acceptedValues={fontValues}
                                />
                                <Item name="separator" />
                                <Item name="bold" />
                                <Item name="italic" />
                                <Item name="strike" />
                                <Item name="underline" />
                                <Item name="separator" />
                                <Item name="alignLeft" />
                                <Item name="alignCenter" />
                                <Item name="alignRight" />
                                <Item name="alignJustify" />
                                <Item name="separator" />
                                <Item name="orderedList" />
                                <Item name="bulletList" />
                                <Item name="separator" />
                                <Item
                                    name="header"
                                    acceptedValues={headerValues}
                                />
                                <Item name="separator" />
                                <Item name="color" />
                                <Item name="background" />
                                <Item name="separator" />
                                <Item name="link" />
                                <Item name="image" />
                                <Item name="separator" />
                                <Item name="clear" />
                                <Item name="codeBlock" />
                                <Item name="blockquote" />
                                <Item name="separator" />
                                <Item name="insertTable" />
                                <Item name="deleteTable" />
                                <Item name="insertRowAbove" />
                                <Item name="insertRowBelow" />
                                <Item name="deleteRow" />
                                <Item name="insertColumnLeft" />
                                <Item name="insertColumnRight" />
                                <Item name="deleteColumn" />
                            </Toolbar>
                        </HtmlEditor>
                    </div>
                    <div className={'board-edit-file-wrapper'}>

                    </div>
                    <div className={'board-edit-button-wrapper'}>
                        <button className={'board-edit-button-light'} onClick={goToList}>취소</button>
                        { !brdId && <button className={'board-edit-button-primary'} onClick={saveContent}>저장</button> }
                        { brdId && <button className={'board-edit-button-primary'} onClick={updateContent}>수정</button> }
                    </div>
                </div>
            }
        </div>
    );
}

export default BoardEditContent;
